/**
 * Fixed Nav.
 * 
 * @module header
 */

import $ from 'jquery';
import mergeOptions from '../lib/merge-options';
import Keyboard from './keyboard';
import throttle from 'lodash/throttle';
import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'stickyNav';
export const DEFAULTS = {
    stickyElement: '',
    fixedHeader: '.js-fixed-header',
    navLinks: '.js-sticky-nav-link',
    sections: '.js-sticky-nav-section',
};

/**
 * Unfix Site's Header
 * @param  {jQuery} $el - header element
 */
const unStickyNav = ($el) => {
    $el.css({ position: 'static' });
};

/**
 * Make Navigation Sticky
 * @param {jQuery} $el - header element
 * @param {jQuery} $html - html element
 */
const setStickyPosition = ($el, $html) => {
    $el.css({ position: 'sticky' });
};

/**
 * Update Active Navigation Link
 * @param {jQuery} $el - header element
 * @param {jQuery} $window - window element
 * @param {Object} opts - options object
 */
const updateActiveNav = ($el, $window, opts) => {
    const { fixedHeader, navLinks, sections } = opts;
    const fixedHeaderHeight = $(fixedHeader).outerHeight();
    const stickyNavHeight = $el.outerHeight();
    const windowScrollTop = $window.scrollTop();
    const sectionsArray = $(sections).toArray();

    sectionsArray.forEach((section) => {
        const sectionTop = $(section).offset().top - fixedHeaderHeight - stickyNavHeight - 50;
        const sectionBottom = sectionTop + $(section).outerHeight();

        if (windowScrollTop >= sectionTop && windowScrollTop < sectionBottom) {
            const sectionId = $(section).attr('id');
            $(navLinks).removeClass('active');
            $(`a[href="#${sectionId}"]`).addClass('active');
        }
    });
};

/**
 * Handle Tab Scrolling
 * @param {Event} evt - event object
 * @param {jQuery} $el - header element
 * @param {Object} opts - options object
 */
const handleSectionScroll = (evt, $el, opts) => {
    evt.preventDefault();
    const { fixedHeader } = opts;
    const $sectionClicked = $(evt.currentTarget.hash);
    const fixedHeaderHeight = $(fixedHeader).outerHeight();
    const stickyNavHeight = $el.outerHeight();

    $('html, body').animate({
        scrollTop: $sectionClicked.offset().top - (fixedHeaderHeight + stickyNavHeight)
    }, 500);
};

/**
 * Update Scroll Position
 * @param {jQuery} $el - header element
 * @param {jQuery} $window - window element
 * @param {Object} opts - options object
 */
const scrollPositionHandler = ($el, $window, opts) => {
    updateActiveNav($el, $window, opts);
};

/**
 * Set Navigation Offset
 * @param {jQuery} $el - header element
 * @param {Object} opts - options object
 */
const setNavOffsetValue = ($el, opts) => {
    const { fixedHeader } = opts;
    const fixedHeaderHeight = $(fixedHeader).outerHeight();
    $el.css({ top: `${fixedHeaderHeight}px` });
};

/**
 * Handle Tab Key Events
 * @param {jQuery} $el - header element
 * @param {Event} evt - key event
 */
const tabEventHandler = ($el, evt) => {
    if (['TAB', 'SHIFT_TAB'].includes(Keyboard.parseKey(evt))) {
        unStickyNav($el);
    }
};

/**
 * Set Event Listeners
 * @param {jQuery} $el - header element
 * @param {jQuery} $html - html element
 * @param {jQuery} $window - window element
 * @param {jQuery} $body - body element
 * @param {Object} opts - options object
 */
const setEventListeners = ($el, $html, $window, opts) => {
    const { navLinks } = opts;
    $html.on('mousedown.stickyNav', () => setStickyPosition($el, $html));
    $html.on('keyup.stickyNav', (evt) => tabEventHandler($el, evt));
    $window.on('scroll.stickyNav', throttle(() => scrollPositionHandler($el, $window, opts), 100));
    $(navLinks).on('click', (evt) => handleSectionScroll(evt, $el, opts));
};

/**
 * Initialize Fixed Navigation
 * @param {HTMLElement} el - header element
 * @param {Object} options - options object
 */
export default function stickyNav(el, options = {}) {
    const $html = $('html');
    const $window = $(window);
    const $el = DEFAULTS.stickyElement ? $(DEFAULTS.stickyElement) : $(el);
    const opts = mergeOptions(DEFAULTS, options, $el);

    setNavOffsetValue($el, opts);
    setEventListeners($el, $html, $window, opts);
}

// Register jQuery plugin
registerJQueryPlugin(PLUGIN_NAME, stickyNav);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/sticky-nav.js